import { apiClient } from '@/api/client';
import type { SelfIdAnswer } from '../constants';

/**
 * Create-startup API surface (MYN-46). `workEmail` is still required for the
 * Backend's `assertWorkEmail` domain check - the OTP verification step has
 * been removed (Task 9). Every profile field is optional at creation
 * (data-fields.md).
 */
export interface CreateStartupPayload {
  companyName: string;
  workEmail: string;
  briefDescription?: string;
  companyWebsiteUrl?: string;
  companyLinkedinUrl?: string;
  sectorId?: number;
  subsectorIds?: number[];
  /** Free text, only when the primary sector is "Other" (max 100 chars). */
  sectorOther?: string;
  fundingStage?: string;
  yearFounded?: number;
  hqCityId?: number;
  hqStateId?: number;
  hqCountryId?: number;
  marketModels?: string[];
  /**
   * Raw S3 key from the logo upload. The Backend's create DTO validates this
   * against the startup-logo key pattern and resolves it into the read-only
   * `logoUrl` on the way back out - `logoUrl` is never accepted on write.
   */
  logoFileKey?: string;
  pitchDeckFileKey?: string;
  pitchDeckFileSizeBytes?: number;
  /** External link (A-58), never an upload key. */
  pitchVideoUrl?: string;
  totalRevenueRange?: string;
  arr?: number;
  amountRaisedToDate?: number;
  nondilutiveFunding?: number;
  recentMilestone?: string;
  aiClassification?: string;
  revenueModel?: string;
  revenueGrowthRate?: string;
  payingCustomers?: string;
  investorTypesSought?: string[];
  /** 3-value answer, never a boolean (a boolean now 400s). Absent = unanswered. */
  founderFemale?: SelfIdAnswer;
  founderUnderrepresented?: SelfIdAnswer;
  founderUnderrepresentedCategories?: string[];
  employeeCount?: number;
  useOfFunds?: string;
  financeSoughtLow?: number;
  financeSoughtHigh?: number;
  valuationOrCap?: number;
  instrument?: string;
  targetCloseDate?: string;
  hasLeadInvestor?: boolean;
  leadInvestorName?: string;
  foundingTeam?: FoundingTeamMemberPayload[];
}

export interface FoundingTeamMemberPayload {
  name: string;
  title: string;
  linkedinUrl?: string;
  pastExits?: boolean;
  // No `photoUrl` (dropped Backend-side, MYN-175) and no `bio` (removed from
  // the product, UX sheet r127).
}

export interface CreatedStartup {
  id: string;
  entityId: string;
  companyName: string;
}

/**
 * Look-ahead check so the Create Startup wizard can be gated before the user
 * fills any of it out, instead of only discovering a block on final submit.
 * Resolves when eligible; throws (STARTUP_DOMAIN_ALREADY_REGISTERED, FR-13)
 * when the caller's company already has a startup profile.
 */
export async function checkStartupEligibility(): Promise<void> {
  await apiClient.get('/startups/eligibility');
}

/**
 * Ask the existing startup (matched by the caller's own work-email domain,
 * same lookup `checkStartupEligibility` already ran) to add the caller as a
 * team member - shown from `StartupAlreadyExistsState` once eligibility
 * fails with STARTUP_DOMAIN_ALREADY_REGISTERED. Fire-and-forget: notifies
 * every team member holding the invite permission (email + in-app alert),
 * no pending-request record is kept - the admin still uses the existing
 * "Invite a Team Member" dialog to actually add them.
 */
export async function requestStartupJoin(): Promise<void> {
  await apiClient.post('/startups/join-request');
}

/** Create the startup entity + profile (Step 4 submit - no OTP gate). */
export async function createStartup(payload: CreateStartupPayload): Promise<CreatedStartup> {
  const { data } = await apiClient.post<CreatedStartup>('/startups', payload);
  return data;
}
